import { Outlet } from 'react-router-dom';
import { useIdentity } from '../providers/IdentityProvider';
import { LoadingScreen } from '../components/feedback/LoadingScreen';
import { ErrorState } from '../ui';
import type { AppError } from '../lib/errors';
import type { AppRole } from '../types/auth';

// Slice P0-H (C3): identity failures are described in Georgian, by cause. Previously this rendered
// `error.message` verbatim — which for the membership failures IS the raw code
// (IDENTITY_MEMBERSHIP_NOT_ACTIVE), i.e. a machine string shown to an operator. The invited case
// is separated out because it is not an error at all from the user's side: their account exists
// and simply awaits activation, and saying so is the difference between a dead end and a wait.
function identityErrorText(error: AppError | null): string {
  switch (error?.code) {
    case 'IDENTITY_MEMBERSHIP_INVITED':
      return 'თქვენი ანგარიში ჯერ არ არის გააქტიურებული.';
    case 'IDENTITY_MEMBERSHIP_NOT_ACTIVE':
      return 'თქვენი წვდომა ამ კომპანიაზე შეჩერებულია. დაუკავშირდით ადმინისტრატორს.';
    case 'IDENTITY_NOT_FOUND':
      return 'თქვენი ანგარიში ამ კომპანიაში ვერ მოიძებნა. დაუკავშირდით ადმინისტრატორს.';
    default:
      return 'სცადეთ გვერდის განახლება. თუ პრობლემა გრძელდება, დაუკავშირდით ადმინისტრატორს.';
  }
}

// Role-aware route guard sourced from the backend identity (roles from user_roles/roles).
// UI gating only — RLS + *_authorize RPCs remain the enforcement boundary. Renders inline
// states (no redirect) so an identity error or role-less user cannot cause a redirect loop.
export function RequireRole({ allow }: { allow: readonly AppRole[] }): JSX.Element {
  const { status, identity, error } = useIdentity();

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }
  if (status === 'error') {
    const invited = error?.code === 'IDENTITY_MEMBERSHIP_INVITED';
    return (
      <ErrorState
        title={invited ? 'ანგარიში ჯერ არ არის აქტიური' : 'იდენტობის ჩატვირთვა ვერ მოხერხდა'}
        description={identityErrorText(error)}
      />
    );
  }
  const permitted = identity !== null && identity.roles.some((role) => allow.includes(role));
  if (!permitted) {
    return <ErrorState title="წვდომა შეზღუდულია" description="ამ გვერდზე წვდომა არ გაქვთ" />;
  }
  return <Outlet />;
}
